import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { Button } from '@3plearning/react-components';
import moment from 'moment';
import {
  ModalBody,
  StudentDetails,
  StudentName,
  LoaderWrapper,
  ActivityName,
  ResultsWrapper,
  ResultsHeading,
  AttemptsWrapper,
  AssignedActivityWrapper,
  Footer,
  ActivityProgressWrapper,
  ActivityProgressTitle,
  ChartWrapper,
  IconWrapper
} from './results.styled';
import PulseLoader from '../pulse-loader';
import ResultCell from '../result-cell/results-cell';
import { dismissModal } from '../../containers/modal-manager/modal-manager-actions';
import LineChart from '../line-chart/line-chart';
import {
  Highest,
  Lowest,
  AssignStart,
  AssignEnd,
  Attempts,
  Period,
  Latest,
  Results,
  Progress
} from '../../helpers/icon-dictionary';
import SvgIcon from '../svg-icon/svg-icon';

const ResultsModal = ({ i18n, isAssigned }) => {
  const wraperRef = useRef(null);
  const dispatch = useDispatch();

  const isLoadingResults = useSelector(
    (state) => state.CourseAssignReducer.isLoadingResults
  );
  const activityReport = useSelector(
    (state) => state.CourseAssignReducer.singleActivityReport
  );

  if (isLoadingResults) {
    return (
      <ModalBody ref={wraperRef}>
        <LoaderWrapper>
          <PulseLoader />
        </LoaderWrapper>
      </ModalBody>
    );
  }

  const AssignedActivity = () => {
    return isAssigned ? (
      <ResultCell
        container
        title={i18n._(t('results."assignedPeriod')`Assigned Period`)}
        icon={Period}
      >
        <ResultCell
          icon={AssignStart}
          title={i18n._(t('results.start')`Start`)}
          value={moment(activityReport?.recentAssignment?.startDate).format(
            'D MMM YYYY h:mm A'
          )}
        />
        <ResultCell
          icon={AssignEnd}
          title={i18n._(t('results.due')`Due`)}
          value={moment(activityReport?.recentAssignment?.dueDate).format(
            'D MMM YYYY h:mm A'
          )}
        />
      </ResultCell>
    ) : (
      <ResultCell
        container
        title={i18n._(t('results."completed')`Completed`)}
        icon={Period}
      >
        <ResultCell
          icon={AssignStart}
          title={i18n._(t('results."lastActivity')`Last Activity`)}
          value={moment(
            activityReport?.recentResults[0]?.completionDate
          ).format('D MMM YYYY h:mm A')}
        />
      </ResultCell>
    );
  };

  return (
    <ModalBody ref={wraperRef} data-testid="result-modal">
      <StudentDetails>
        <StudentName data-testid="student-name">
          {activityReport.studentFirstName} {activityReport.studentLastName}
        </StudentName>
        <ActivityName data-testid="activity-name">
          {activityReport.activityName}
        </ActivityName>
      </StudentDetails>
      <ResultsHeading data-testid="results">
        <IconWrapper>
          <SvgIcon icon={Results} />
        </IconWrapper>
        {i18n._(t('Results')`Results`)}
      </ResultsHeading>
      <ResultsWrapper>
        <AttemptsWrapper>
          <ResultCell
            icon={Attempts}
            title={i18n._(t('results.attempts')`Attempts`)}
            value={activityReport.totalAttempts}
          />
          <ResultCell
            icon={Lowest}
            title={i18n._(t('results.lowestAttempt')`Lowest Attempt`)}
            value={`${activityReport.lowestScore}%`}
          />
          <ResultCell
            icon={Latest}
            title={i18n._(t('results.lastAttempt')`Last Attempt`)}
            value={`${activityReport.lastScore}%`}
          />
          <ResultCell
            icon={Highest}
            title={i18n._(t('results.highestAttempt')`Highest Attempt`)}
            value={`${activityReport.highestScore}%`}
          />
        </AttemptsWrapper>
        <AssignedActivityWrapper>
          {AssignedActivity()}
          <ActivityProgressWrapper>
            <ActivityProgressTitle>
              <IconWrapper>
                <SvgIcon icon={Progress} />
              </IconWrapper>
              {i18n._(t('results.activityProgress')`Activity Progress`)}
            </ActivityProgressTitle>
            <ChartWrapper>
              <LineChart results={activityReport?.recentResults} i18n={i18n} />
            </ChartWrapper>
          </ActivityProgressWrapper>
        </AssignedActivityWrapper>
      </ResultsWrapper>
      <Footer>
        <Button
          className="okButton"
          data-testid="ok-button"
          primary
          type={Button.LOZENGE}
          text={i18n._(t('results.ok')`OK`)}
          onClick={() => {
            dispatch(dismissModal());
          }}
        />
      </Footer>
    </ModalBody>
  );
};

export const viewResultsModal = (i18n, isAssigned) => ({
  content: <ResultsModal i18n={i18n} isAssigned={isAssigned} />,
  id: 0,
  buttons: []
});

ResultsModal.propTypes = {
  i18n: PropTypes.object.isRequired,
  isAssigned: PropTypes.bool.isRequired
};

export default ResultsModal;



// WEBPACK FOOTER //
// ./components/results-modal/results.modal.js